import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import { useUser } from "../../contexts/UserContext";
import { useAdminContext } from "../../contexts/AdminContext";
import { useLogsContext } from "../../contexts/LogsContext";
import Loader from "../../utils/Loader";
import {
  findExistingClient,
  hasClientDataChanged,
  generateNextCID,
} from "../../utils/Utility";
import AppLauncherMenu from "../../utils/AppLauncherMenu";
import LogsList from "./LogsList";
import LogsForm from "./LogsForm";
import { db } from "../../firebase";
import ConfirmDeleteModal from "../ConfirmDeleteModal";
import { collection, addDoc, updateDoc, doc } from "firebase/firestore";
import { selectStyles, LOG_CONFIGS } from "../Constants";
import { toast } from "react-toastify";
import LogsTemplateSection from "./LogsTemplateSection";

const LogsPage = ({ setNavbarButtons }) => {
  const { group, type, logId } = useParams();
  const navigate = useNavigate();
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [appBarButtons, setAppBarButtons] = useState([]);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState("");
  const [confirmationText, setConfirmationText] = useState("");
  const config = LOG_CONFIGS[type];

  // Redirect if invalid type or if this is not a logs route
  useEffect(() => {
    if (!config) {
      navigate("/records/secretarial-records", { replace: true });
    }
  }, [type, config, navigate]);

  const {
    userData,
    user,
    invoiceClients,
    updateInvoiceClient,
    addInvoiceClient,
  } = useUser();

  const {
    loading: logsLoading,
    loadingText: logsLoadingText,
    setLoadingText,
    saveLogs,
    loadLogs,
    deleteLog,
    saveTemplate,
    deleteTemplate,
    loadTemplates,
    getCachedLogs,
    getTemplates,
    clearCache,
  } = useLogsContext();

  const {
    selectedUserId,
    isViewingOtherUser,
    handleUserSelection,
    getSelectedUserName,
    getAvailableUsers,
    apLoading,
    adminData,
    selectedUserData,
    updateUserInvoiceClient,
    addUserInvoiceClient,
  } = useAdminContext();

  const [rows, setRows] = useState([]);
  const [dropdownLists, setDropdownLists] = useState(() => {
    const lists = {};
    if (config?.dropdownLists) {
      config.dropdownLists.forEach((listKey) => {
        lists[listKey] = [];
      });
    }
    return lists;
  });

  const [modifiedDropdownLists, setModifiedDropdownLists] = useState(new Set());
  const targetUserId = isViewingOtherUser ? selectedUserId : userData?.uid;
  const templates = getTemplates(targetUserId || "");

  const currentInvoiceClients = isViewingOtherUser
    ? adminData?.userClients
    : invoiceClients;
  const currentUser = isViewingOtherUser ? selectedUserData : user;
  const currentUpdateClient = isViewingOtherUser
    ? updateUserInvoiceClient
    : updateInvoiceClient;
  const currentAddClient = isViewingOtherUser
    ? addUserInvoiceClient
    : addInvoiceClient;
  const [viewMode, setViewMode] = useState("list");
  const [currentLog, setCurrentLog] = useState(null);

  // Admin user selection
  const selectOptions = React.useMemo(() => {
    const options = [{ value: "default", label: "My Dashboard" }];
    getAvailableUsers().forEach((user) => {
      options.push({
        value: user.uid,
        label: user.name,
      });
    });
    return options;
  }, [getAvailableUsers]);

  const selectedOption = React.useMemo(() => {
    return (
      selectOptions.find((option) => option.value === selectedUserId) ||
      selectOptions[0]
    );
  }, [selectOptions, selectedUserId]);

  const handleReactSelectChange = async (selectedOption) => {
    if (!selectedOption) return;

    const result = await handleUserSelection(selectedOption.value);

    if (!result.success) {
      if (result.permissionDenied) {
        toast.error(result.message || "Access denied to logs");
      } else {
        toast.error("Failed to load user data");
      }
    }
  };

  const handleOpenTemplate = useCallback((template = null) => {
    setSelectedTemplate(template);
    setIsTemplateModalOpen(true);
  }, []);

  const handleTemplateClick = (template) => {
    navigate(`/${group}/${type}/new`, {
      state: { template },
    });
  };

  const handleSaveTemplate = async (updatedTemplate) => {
    const targetUserId = isViewingOtherUser ? selectedUserId : userData.uid;

    if (!targetUserId) {
      toast.error("User ID not found");
      return;
    }

    await saveTemplate(targetUserId, updatedTemplate);
  };

  const handleDeleteTemplate = async (templateId) => {
    const targetUserId = isViewingOtherUser ? selectedUserId : userData.uid;

    await deleteTemplate(targetUserId, templateId);
  };

  useEffect(() => {
    const loadTemplatesData = async () => {
      const targetUserId = isViewingOtherUser ? selectedUserId : userData?.uid;

      if (!targetUserId || !type) return;

      await loadTemplates(targetUserId);
    };

    loadTemplatesData();
  }, [selectedUserId, isViewingOtherUser, userData, type, loadTemplates]);

  // Load logs data
  useEffect(() => {
    const targetUserId = isViewingOtherUser ? selectedUserId : userData?.uid;

    if (targetUserId && config) {
      // Check cache first
      const cached = getCachedLogs(targetUserId, type);

      if (cached) {
        // Use cached data
        // Check if dropdownLists is nested or at root level
        if (cached.dropdownLists) {
          // Nested structure
          setDropdownLists(cached.dropdownLists);
        } else {
          // Root level structure (old format)
          const loadedLists = {};
          config.dropdownLists.forEach((listKey) => {
            loadedLists[listKey] = cached[listKey] || [];
          });
          setDropdownLists(loadedLists);
        }
        setRows(cached.rows || []);
      } else {
        // Load from database
        loadLogs(targetUserId, type).then((result) => {
          if (result.success && result.data) {
            // Access nested dropdownLists
            if (result.data.dropdownLists) {
              setDropdownLists(result.data.dropdownLists);
            } else {
              // Fallback to root level (shouldn't happen with new format)
              const loadedLists = {};
              config.dropdownLists.forEach((listKey) => {
                loadedLists[listKey] = result.data[listKey] || [];
              });
              setDropdownLists(loadedLists);
            }
            setRows(result.data.rows || []);
          }
        });
      }
    }
  }, [
    selectedUserId,
    isViewingOtherUser,
    userData,
    type,
    loadLogs,
    config,
    getCachedLogs,
  ]);

  useEffect(() => {
    if (selectedUserId) {
      clearCache(selectedUserId);
    }
  }, [selectedUserId, clearCache]);

  // Handle navigation based on logId param
  useEffect(() => {
    if (!logId || logsLoading) return;

    if (logId === "new") {
      setViewMode("form");
      setCurrentLog(null);
      return;
    }

    if (rows.length === 0) {
      return;
    }

    const log = rows.find((r) => r.firebaseId === logId);

    if (log) {
      setViewMode("form");
      setCurrentLog(log);
    } else {
      toast.error("Log not found");
      navigate(`/${group}/${type}`, { replace: true });
    }
  }, [logId, rows, type, navigate, group, logsLoading]);

  const handleRowClick = (log) => {
    navigate(`/${group}/${type}/${log.firebaseId}`);
  };

  const handleCancel = useCallback(() => {
    navigate(`/${group}/${type}`);
  }, [group, type, navigate]);

  const saveOrUpdateClient = useCallback(
    async (clientData) => {
      try {
        if (!clientData.name || clientData.name.trim() === "") {
          return null; // Don't save empty clients
        }

        const existingClient = findExistingClient(
          clientData,
          currentInvoiceClients
        );

        const clientToSave = {
          name: clientData.name.trim(),
          address: clientData.address?.trim() || "",
          tinNo: clientData.tinNo?.trim() || "",
          contact: clientData.contact?.trim() || "",
          rep: clientData.rep?.trim() || "",
          email: clientData.email?.trim() || "",
        };

        if (existingClient) {
          // Check if any data has changed
          if (hasClientDataChanged(clientData, existingClient)) {
            // Update existing client (preserve existing CID)
            const targetUserId = isViewingOtherUser
              ? selectedUserId
              : currentUser.uid;
            const clientRef = doc(
              db,
              "users",
              targetUserId,
              "invoiceClients",
              existingClient.id
            );
            await updateDoc(clientRef, {
              ...clientToSave,
              updatedAt: new Date().toISOString(),
            });

            const updatedClient = {
              id: existingClient.id,
              cid: existingClient.cid,
              ...clientToSave,
              updatedAt: new Date().toISOString(),
            };

            // Update the state with the updated client data using appropriate method
            currentUpdateClient(existingClient.id, {
              ...clientToSave,
              updatedAt: new Date().toISOString(),
            });

            toast.info("Client information updated.");
            return updatedClient;
          } else {
            return existingClient;
          }
        } else {
          // Create new client with generated CID
          const newCID = generateNextCID(currentInvoiceClients);
          const targetUserId = isViewingOtherUser
            ? selectedUserId
            : currentUser.uid;

          const newClientData = {
            ...clientToSave,
            cid: newCID,
            createdAt: new Date().toISOString(),
          };

          const clientRef = await addDoc(
            collection(db, "users", targetUserId, "invoiceClients"),
            newClientData
          );

          const savedClient = { id: clientRef.id, ...newClientData };

          // Add the new client to the state using appropriate method
          currentAddClient(savedClient);

          toast.success(`New client added to your database (${newCID}).`);
          return savedClient;
        }
      } catch (error) {
        console.error("Error saving/updating client:", error);
        toast.warn(
          "Invoice saved, but failed to save/update client information."
        );
        return null;
      }
    },
    [
      currentInvoiceClients,
      currentUser,
      currentAddClient,
      currentUpdateClient,
      isViewingOtherUser,
      selectedUserId,
    ]
  );

  const handleSave = async (formData) => {
    const targetUserId = isViewingOtherUser ? selectedUserId : userData.uid;

    if (!targetUserId) {
      toast.error("User ID not found");
      return;
    }

    // Save or update client if needed
    if (formData.to.name && formData.to.name.trim() !== "") {
      setLoadingText("Processing client information...");
      await saveOrUpdateClient(formData.to);
    }

    let updatedRows;
    const changedRows = new Set();

    if (currentLog) {
      // Edit existing log
      updatedRows = rows.map((row) => {
        if (row.id === currentLog.id) {
          changedRows.add(row.id);
          return { ...formData, id: row.id, firebaseId: row.firebaseId };
        }
        return row;
      });
    } else {
      // Create new log
      const newLog = {
        ...formData,
        id: Date.now(),
        firebaseId: null,
        createdAt: new Date().toISOString(),
      };
      updatedRows = [...rows, newLog];
      changedRows.add(newLog.id);
    }

    // Always save dropdown lists if any were modified
    const dropdownListsToSave =
      modifiedDropdownLists.size > 0 ? dropdownLists : {};

    const result = await saveLogs(targetUserId, type, {
      dropdownLists: dropdownListsToSave,
      rows: updatedRows,
      changedRows,
      deletedRows: new Set(),
    });

    if (result.success) {
      // Update local state with the potentially updated rows (containing new firebaseIds)
      setRows(result.updatedRows || updatedRows);
      setModifiedDropdownLists(new Set());
      toast.success(
        currentLog ? "Log updated successfully" : "Log created successfully"
      );
      navigate(`/${group}/${type}`);
    }
  };

  const handleDelete = async (logId) => {
    const targetUserId = isViewingOtherUser ? selectedUserId : userData.uid;

    if (!targetUserId) {
      toast.error("User ID not found");
      return;
    }

    const logToDelete = rows.find((r) => r.id === logId);

    if (!logToDelete) {
      toast.error("Log not found");
      return;
    }

    const result = await deleteLog(
      targetUserId,
      type,
      logId,
      logToDelete.firebaseId
    );

    if (result.success) {
      const updatedRows = rows.filter((row) => row.id !== logId);
      setRows(updatedRows);
      setModifiedDropdownLists(new Set());

      // If we're viewing the deleted log, go back to list
      if (logId === currentLog?.id) {
        navigate(`/${group}/${type}`);
      }
    }
  };

  const requestDelete = (type, id) => {
    setDeleteType(type);
    setDeleteTarget(id);
    setConfirmationText(
      `Are you sure you want to delete this ${type}? This action cannot be undone.`
    );
    setConfirmDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setConfirmDeleteModalOpen(false);
    setDeleteTarget(null);
    setDeleteType("");
  };

  const handleConfirmDelete = async () => {
    try {
      if (deleteType === "log") {
        await handleDelete(deleteTarget);
      } else if (deleteType === "template") {
        await handleDeleteTemplate(deleteTarget);
      }
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error(`Failed to delete ${deleteType}. Please try again.`);
    } finally {
      setConfirmDeleteModalOpen(false);
      setDeleteTarget(null);
      setDeleteType("");
    }
  };

  const handleAddToDropdown = (listKey, value, isReplace = false) => {
    if (isReplace) {
      setDropdownLists((prev) => ({
        ...prev,
        [listKey]: value,
      }));
    } else {
      setDropdownLists((prev) => ({
        ...prev,
        [listKey]: [...(prev[listKey] || []), value],
      }));
    }
    setModifiedDropdownLists((prev) => new Set(prev).add(listKey));
  };

  const handleNewLog = useCallback(
    async (templateId) => {
      // console.log("New Log : ", templateId);
      if (!templateId) return;

      const matchedTemplate = templates.find((t) => t.id === templateId);

      navigate(`/${group}/${type}/new`, {
        state: { template: matchedTemplate },
      });
    },
    [group, type, navigate, templates]
  );

  if (!config) return null;

  return (
    <>
      <Helmet>
        <title>{config.pageTitle}</title>
      </Helmet>

      {/* Navigation Bar */}
      {viewMode === "list" && (
        <nav className="w-full bg-white/10">
          <div className="px-3 py-4 max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex gap-1">
              {appBarButtons.map((btn) => (
                <button
                  key={btn.label}
                  onClick={btn.onClick}
                  disabled={isViewingOtherUser && btn.requiresOwnership}
                  className={`flex flex-row items-center border justify-center px-3 rounded shadow shadow-tier-secondary bg-tier-primary dark:bg-tier-primary-dark dark:border-gray-600 hover:bg-white/20 ${
                    isViewingOtherUser && btn.requiresOwnership
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  <span className="material-symbols-outlined text-base">
                    {btn.icon}
                  </span>
                  <p className="ml-2 font-medium text-xs text-center hidden sm:block">
                    {btn.label}
                  </p>
                </button>
              ))}
            </div>

            <div className="flex flex-row items-center gap-2">
              <div className="min-w-64">
                <Select
                  value={selectedOption}
                  onChange={handleReactSelectChange}
                  options={selectOptions}
                  styles={selectStyles}
                  classNamePrefix="react-select"
                  isSearchable={true}
                  isLoading={apLoading}
                  isDisabled={apLoading}
                  placeholder="Select user..."
                  noOptionsMessage={() => "No users found"}
                  loadingMessage={() => "Loading users..."}
                />
              </div>
              <AppLauncherMenu />
            </div>
          </div>
        </nav>
      )}

      <div className="p-2">
        {(logsLoading || apLoading) && <Loader text={logsLoadingText} />}

        {/* Admin Mode Indicator */}
        {isViewingOtherUser && (
          <div className="mb-4 p-3 border-l-4 rounded-r-md bg-teal-100 dark:bg-teal-900 border-teal-500 text-teal-700 dark:text-teal-200">
            <div className="flex items-center">
              <span className="material-symbols-outlined mr-2">
                admin_panel_settings
              </span>
              <div>
                <p>
                  <span className="font-medium">Admin Mode: Managing </span>
                  <span className="font-bold text-lg dark:text-tier-secondary text-amber-600">
                    {getSelectedUserName()}
                  </span>
                  <span className="font-medium">
                    's {config.title.toLowerCase()}
                  </span>
                </p>
                <p className="text-sm mt-1 opacity-90">
                  Full admin access: You can view, create, edit, and delete.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {viewMode === "list" ? (
          <>
            <LogsTemplateSection
              templates={templates}
              logType={type}
              onTemplateClick={handleTemplateClick}
              onOpenTemplate={handleOpenTemplate}
              isModalOpen={isTemplateModalOpen}
              selectedTemplate={selectedTemplate}
              setIsModalOpen={setIsTemplateModalOpen}
              onSaveTemplate={handleSaveTemplate}
              onDeleteTemplate={(id) => requestDelete("template", id)}
              setAppBarButtons={setAppBarButtons}
            />

            <LogsList
              logs={rows}
              templates={templates}
              logType={type}
              onDelete={(id) => requestDelete("log", id)}
              onRowClick={handleRowClick}
            />
          </>
        ) : (
          <LogsForm
            setNavbarButtons={setNavbarButtons}
            logType={type}
            logData={currentLog}
            dropdownLists={dropdownLists}
            onSave={handleSave}
            onCancel={handleCancel}
            onAddToDropdown={handleAddToDropdown}
            onNewLog={handleNewLog}
            templates={templates}
            isLoading={logsLoading}
          />
        )}
      </div>

      <ConfirmDeleteModal
        isOpen={confirmDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title="Confirm Delete"
        message={confirmationText}
      />
    </>
  );
};

export default LogsPage;
